import { matchPath } from 'react-router-dom'

type NumericIdString = string
type UUID = string

type MomentResult = { type: 'moment'; id: NumericIdString }
type StoryResult = { type: 'story'; id: NumericIdString }
type ContentResult = { type: 'content'; id: NumericIdString }
type BoardResult = { type: 'board'; id: UUID }

type OpalObjectResult =
  | MomentResult
  | StoryResult
  | ContentResult
  | BoardResult
  | null

export const opalObjectByUrl = (pathname: string): OpalObjectResult => {
  return (
    matchContentPath(pathname) ??
    matchMomentPath(pathname) ??
    matchStoryPath(pathname) ??
    matchBoardPath(pathname)
  )
}

const matchMomentPath = (pathname: string): MomentResult | null => {
  const momentShroudMatch = matchPath(
    '/moments/:workspaceSlug/:momentId',
    pathname
  )

  if (momentShroudMatch !== null && momentShroudMatch.params.momentId) {
    try {
      // moment id segment must be an integer
      ensureInteger(momentShroudMatch.params.momentId)
      return { type: 'moment', id: momentShroudMatch.params.momentId }
    } catch {
      return null
    }
  }

  const storyMomentShroudMatch = matchPath(
    '/moments/:workspaceSlug/stories/:storyId/moments/:momentId',
    pathname
  )

  if (
    storyMomentShroudMatch !== null &&
    storyMomentShroudMatch.params.momentId
  ) {
    try {
      // moment id segment must be an integer
      ensureInteger(storyMomentShroudMatch.params.momentId)
      return { type: 'moment', id: storyMomentShroudMatch.params.momentId }
    } catch {
      return null
    }
  }

  return null
}

const matchStoryPath = (pathname: string): StoryResult | null => {
  const storyCalendarMatch = matchPath(
    '/moments/:workspaceSlug/stories/:storyId',
    pathname
  )

  if (storyCalendarMatch !== null && storyCalendarMatch.params.storyId) {
    try {
      // story id segment must be an integer
      ensureInteger(storyCalendarMatch.params.storyId)
      return { type: 'story', id: storyCalendarMatch.params.storyId }
    } catch {
      return null
    }
  }

  return null
}

export const matchContentPath = (pathname: string): ContentResult | null => {
  const contentShroudMatch = matchPath(
    '/moments/:workspaceSlug/:momentId/posts/:postId',
    pathname
  )

  if (contentShroudMatch !== null && contentShroudMatch.params.postId) {
    try {
      // content id segment must be an integer
      ensureInteger(contentShroudMatch.params.postId)
      return { type: 'content', id: contentShroudMatch.params.postId }
    } catch {
      return null
    }
  }

  const storyContentShroudMatch = matchPath(
    '/moments/:workspaceSlug/stories/:storyId/moments/:momentId/posts/:postId',
    pathname
  )

  if (
    storyContentShroudMatch !== null &&
    storyContentShroudMatch.params.postId
  ) {
    try {
      // content id segment must be an integer
      ensureInteger(storyContentShroudMatch.params.postId)
      return { type: 'content', id: storyContentShroudMatch.params.postId }
    } catch {
      return null
    }
  }

  return null
}

const matchBoardPath = (pathname: string): BoardResult | null => {
  const boardMatch = matchPath(
    '/moments/:workspaceSlug/boards/:boardId',
    pathname
  )

  if (boardMatch !== null && boardMatch.params.boardId) {
    return { type: 'board', id: boardMatch.params.boardId }
  }

  return null
}

const ensureInteger = (value: string) => {
  // regex to test for an integer? Yeah... isNaN and parseInt have
  // quite a bit of baggage when attempting to test string values.
  // https://stackoverflow.com/questions/175739/how-can-i-check-if-a-string-is-a-valid-number
  if (!/^\d+$/.test(value)) {
    throw new Error('URL Segment is not an integer')
  }
}
